import React, { useState, useEffect, useCallback } from 'react';
import { buildAlts, allAltsValid } from '../../lib/alt';
import { VALID_SELF_LABELS } from '../../lib/publish';

const MAX_ALT_LENGTH = 1000;

// Red-team PLAN-08: publish-time self-label picker (moderation controls).
const LABEL_LABELS = {
  sexual: 'Sexual content',
  nudity: 'Nudity',
  porn: 'Porn',
  'graphic-media': 'Graphic media',
};

const AltPanel = ({ total, onAltsChange, onLabelsChange, onPublish }) => {
  const [description, setDescription] = useState('');
  const [overrides, setOverrides] = useState({}); // {sliceIndex: text}
  const [labels, setLabels] = useState([]);

  const alts = buildAlts(total, description, overrides);
  const valid = allAltsValid(alts);

  // Report the computed alts upward (the publish step needs them).
  useEffect(() => {
    onAltsChange?.(alts, valid);
  }, [alts, valid, onAltsChange]);

  useEffect(() => {
    onLabelsChange?.(labels);
  }, [labels, onLabelsChange]);

  const toggleLabel = useCallback((label) => {
    setLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  }, []);

  const onDescriptionChange = useCallback((e) => {
    setDescription(e.target.value.slice(0, MAX_ALT_LENGTH));
  }, []);

  const onOverrideChange = useCallback((i, value) => {
    setOverrides((prev) => {
      const next = { ...prev };

      if (value.trim() === '') {
        delete next[i]; // empty override falls back to the template
      } else {
        next[i] = value.slice(0, MAX_ALT_LENGTH);
      }

      return next;
    });
  }, []);

  return (
    <div className="alt-panel">
      <h3>Alt text</h3>
      <p className="alt-note">
        Every slice needs alt text — a 10-slice panorama is opaque to
        screen-reader users without it. Overrides are optional; empty
        overrides use the template.
      </p>

      <label className="alt-field">
        Description (applies to all slices)
        <input
          type="text"
          value={description}
          onChange={onDescriptionChange}
          placeholder="e.g. Sunset panorama of the harbor"
          aria-label="Shared alt description"
        />
      </label>

      <details className="alt-overrides">
        <summary>Per-slice overrides</summary>
        {Array.from({ length: total }, (_, idx) => {
          const i = idx + 1;

          return (
            <label key={i} className="alt-field">
              Slice {i} of {total}
              <input
                type="text"
                value={overrides[i] || ''}
                onChange={(e) => onOverrideChange(i, e.target.value)}
                aria-label={`Alt text override for slice ${i}`}
              />
            </label>
          );
        })}
      </details>

      <fieldset className="alt-labels">
        <legend>Content labels (optional)</legend>
        <p className="alt-note">
          Slicer posts to a public network — mark sensitive panoramas so
          Bluesky can label them (red-team PLAN-08).
        </p>
        {VALID_SELF_LABELS.map((label) => (
          <label key={label} className="alt-label-option">
            <input
              type="checkbox"
              checked={labels.includes(label)}
              onChange={() => toggleLabel(label)}
              aria-label={LABEL_LABELS[label]}
            />
            {LABEL_LABELS[label]}
          </label>
        ))}
      </fieldset>

      <div className="alt-preview">
        <p>
          <strong>Slice 1 alt:</strong> {alts[0]}
        </p>
        {total > 1 && (
          <p>
            <strong>Slice {total} alt:</strong> {alts[total - 1]}
          </p>
        )}
      </div>

      {!valid && (
        <p className="crop-error" role="alert">
          Every slice needs non-empty alt text before publishing.
        </p>
      )}

      <button
        type="button"
        className="publish-button"
        disabled={!valid}
        onClick={onPublish}
        title={valid ? 'Publish to Bluesky' : 'Add alt text to publish'}
      >
        Publish
      </button>
    </div>
  );
};

export default AltPanel;
